import { useId, useState } from 'react'
import { Crosshair, MapPin, HelpCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UnitMap } from '@/shared/map/UnitMap'
import { hasMapbox } from '@/shared/map/mapbox'
import { captureLocation, accuracyNote } from '@/shared/geo/geolocation'
import type { UnitResponse } from '@/shared/api/types'
import { Field, TextInput, TextArea } from './fields'
import { UnitPicker } from './UnitPicker'
import { label } from '../vocab'

/**
 * Step 3 · Location — where it happened, and **how we know**.
 *
 * ## Why `locationSource` is recorded and not inferred
 *
 * A 2 km wifi estimate and a pin dropped on a map are not the same evidence, and an audit
 * asking "where was this" must be able to tell them apart. Every path below stamps the
 * source it used; nothing here can set a coordinate without also saying where it came
 * from. `coverage/geolocation.ts` — reused, not reimplemented — learned this the same way.
 *
 * ## Four ways through, and one of them is "I don't know"
 *
 * A reporter standing in front of something they cannot place must still be able to file.
 * The escape needs a typed reason and stamps `NONE`; the database constraint enforces the
 * reason, so this is not a client-side courtesy.
 *
 * ## Mapbox may be absent
 *
 * `hasMapbox()` is false with no token. The step still works — the map path hides, the
 * other three stay, and the panel says why the map is not there rather than rendering a
 * grey rectangle.
 */

type Mode = 'gps' | 'map' | 'unit' | 'none'

export function StepLocation({
  value, onChange, errors,
}: {
  value: {
    unitId: string | null
    unitName?: string | null
    latitude: number | null
    longitude: number | null
    locationSourceCode: string | null
    locationAccuracyM: number | null
    locationLabel: string | null
    locationAbsentReason: string | null
  }
  onChange: (patch: Partial<typeof value>) => void
  errors: Record<string, string>
}) {
  const ids = useId()
  const [mode, setMode] = useState<Mode>(() => initialMode(value))
  const [capturing, setCapturing] = useState(false)
  const [captureError, setCaptureError] = useState<string | null>(null)

  const useMyLocation = async () => {
    setCapturing(true)
    setCaptureError(null)
    const outcome = await captureLocation()
    setCapturing(false)
    if (!outcome.ok) {
      // The refusal is shown, and the other paths stay open. A browser that will not give
      // a fix must not be able to stop the report.
      setCaptureError(outcome.reason)
      return
    }
    onChange({
      latitude: outcome.location.latitude,
      longitude: outcome.location.longitude,
      locationAccuracyM: outcome.location.accuracyM,
      locationSourceCode: 'DEVICE_GPS',
      locationAbsentReason: null,
    })
  }

  const pickOnMap = (lat: number, lon: number) => {
    onChange({
      latitude: lat,
      longitude: lon,
      // A pin has no accuracy figure. Carrying the GPS one over would claim a precision
      // the pin does not have.
      locationAccuracyM: null,
      locationSourceCode: 'MAP_PICK',
      locationAbsentReason: null,
    })
  }

  const pickUnit = (unit: UnitResponse | null) => {
    onChange({
      unitId: unit?.id ?? null,
      // Naming a unit does not by itself claim a coordinate. The backend may fall back to
      // the unit centroid, which is honest about being an approximation and stamps
      // UNIT_CENTROID when it does.
      locationAbsentReason: null,
    })
  }

  const declare = (reason: string) => {
    onChange({
      locationAbsentReason: reason || null,
      locationSourceCode: reason ? 'NONE' : null,
      latitude: null,
      longitude: null,
      locationAccuracyM: null,
      unitId: null,
    })
  }

  const placed = value.latitude != null && value.longitude != null

  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="How to record the location" className="flex flex-wrap gap-2">
        <ModeButton active={mode === 'gps'} onClick={() => setMode('gps')} icon={<Crosshair className="h-3.5 w-3.5" />}>
          Use my current location
        </ModeButton>
        <ModeButton active={mode === 'map'} onClick={() => setMode('map')} icon={<MapPin className="h-3.5 w-3.5" />}>
          Pick on the map
        </ModeButton>
        <ModeButton active={mode === 'unit'} onClick={() => setMode('unit')} icon={<MapPin className="h-3.5 w-3.5" />}>
          Name a unit
        </ModeButton>
        <ModeButton active={mode === 'none'} onClick={() => setMode('none')} icon={<HelpCircle className="h-3.5 w-3.5" />}>
          I do not know where
        </ModeButton>
      </div>

      {mode === 'gps' && (
        <div className="space-y-2">
          <button type="button" onClick={() => void useMyLocation()} disabled={capturing}
            className="erp-btn-primary h-9 px-3 inline-flex items-center gap-2 text-erp-sm">
            {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />}
            {capturing ? 'Getting a fix…' : 'Use my current location'}
          </button>
          {captureError && (
            <p className="text-erp-sm text-amber-800 dark:text-amber-400">
              {captureError} Pick on the map, name a unit, or say the location is unknown.
            </p>
          )}
          <p className="text-erp-xs text-muted-foreground/80">
            The browser only offers location on a secure origin (https, or localhost).
          </p>
        </div>
      )}

      {mode === 'map' && (
        hasMapbox() ? (
          <div className="space-y-2">
            <UnitMap mode="point" editable label="Where the incident happened"
              lat={value.latitude} lon={value.longitude}
              onPick={pickOnMap}
              className="h-72 rounded-md overflow-hidden border border-border" />
            <p className="text-erp-xs text-muted-foreground">
              Click to place the pin, or drag it. The map is bounded to Kenya.
            </p>
          </div>
        ) : (
          <p className="text-erp-sm text-muted-foreground border-l-2 border-border pl-3 py-1">
            The map is not available — this deployment has no Mapbox token configured. The
            other three ways of recording a location all work, and the latitude and
            longitude boxes below take a coordinate directly.
          </p>
        )
      )}

      {mode === 'unit' && <UnitPicker value={value.unitId} onPick={pickUnit} />}

      {mode === 'none' && (
        <Field label="Why the location is not known" htmlFor={`${ids}-absent`}
          error={errors.locationAbsentReason}
          hint="Required for this path. “A caller described it but could not name the place” is a real answer; a blank is not.">
          <TextArea id={`${ids}-absent`} rows={3} max={500}
            value={value.locationAbsentReason ?? ''}
            onChange={declare} />
        </Field>
      )}

      {/* Always visible: what the step has actually recorded, and how. Every path above
          writes into these, so the reporter can see what the desk will receive. */}
      <div className="erp-card p-3 space-y-3">
        <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground">
          Recorded
        </div>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <Field label="Latitude" error={errors.latitude}>
            <TextInput type="number" value={value.latitude?.toString() ?? ''}
              onChange={(v) => onChange({
                latitude: v === '' ? null : Number(v),
                locationSourceCode: value.locationSourceCode ?? 'MAP_PICK',
              })} />
          </Field>
          <Field label="Longitude" error={errors.longitude}>
            <TextInput type="number" value={value.longitude?.toString() ?? ''}
              onChange={(v) => onChange({
                longitude: v === '' ? null : Number(v),
                locationSourceCode: value.locationSourceCode ?? 'MAP_PICK',
              })} />
          </Field>
          <Field label="How it was obtained">
            <div className="h-9 flex items-center text-erp-sm">
              {value.locationSourceCode ? label(value.locationSourceCode) : '— nothing recorded yet'}
            </div>
          </Field>
        </div>

        {placed && value.locationAccuracyM != null && (
          <p className="text-erp-xs text-muted-foreground">{accuracyNote(value.locationAccuracyM)}</p>
        )}

        <Field label="Place, in words" htmlFor={`${ids}-label`} error={errors.locationLabel}
          hint="Optional. “Outside the gate at Kiamumbi Primary” — what a person would say.">
          <TextInput id={`${ids}-label`} max={200} value={value.locationLabel ?? ''}
            onChange={(v) => onChange({ locationLabel: v || null })} />
        </Field>

        {errors.location && (
          <p className="text-erp-sm text-red-700 dark:text-red-400">{errors.location}</p>
        )}
      </div>
    </div>
  )
}

function initialMode(v: { unitId: string | null; latitude: number | null; locationAbsentReason: string | null }): Mode {
  if (v.locationAbsentReason) return 'none'
  if (v.latitude != null) return 'map'
  if (v.unitId) return 'unit'
  return 'gps'
}

function ModeButton({
  active, onClick, icon, children,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick}
      className={cn(
        'h-9 px-3 inline-flex items-center gap-2 rounded-md border text-erp-sm',
        active
          ? 'border-chocolate/60 bg-chocolate-soft/40 text-chocolate font-medium'
          : 'border-input bg-background text-muted-foreground hover:text-foreground',
      )}>
      {icon}
      {children}
    </button>
  )
}
